import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Delete-or-request helper. If `userRole === "admin"`, performs a direct hard delete
 * via the entity's standard `DELETE` endpoint (which is now a soft-delete server-side).
 * Any other role submits a deletion approval request that an admin must approve.
 *
 * @param {string} entityType   "vehicle" | "battery" | "tyre" | "mileage_log"
 * @param {string} entityId     UUID of the entity to delete
 * @param {string} entityLabel  Human-readable label used in confirmation text
 * @param {string} userRole     Current user's role (from AuthContext)
 * @returns {Promise<{ok: boolean, requested: boolean}>}
 */
export async function requestOrDelete(entityType, entityId, entityLabel, userRole) {
    const endpointMap = {
        vehicle: "vehicles",
        battery: "batteries",
        tyre: "tyres",
        mileage_log: "mileage",
    };
    if (userRole === "admin") {
        if (!window.confirm(`Delete ${entityLabel}? This is a soft delete — the record is hidden but history is preserved.`))
            return { ok: false };
        try {
            await api.delete(`/${endpointMap[entityType]}/${entityId}`);
            toast.success(`${entityLabel} deleted`);
            return { ok: true, requested: false };
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
            return { ok: false };
        }
    }
    const reason = window.prompt(
        `Submit a deletion request for ${entityLabel}?\n\nAn admin must approve before the record is removed.\nOptional: add a reason for the audit log.`,
        ""
    );
    if (reason === null) return { ok: false };
    try {
        await api.post("/approvals", {
            entity_type: entityType,
            entity_id: entityId,
            action: "delete",
            reason: reason.trim() || null,
        });
        toast.success("Deletion request submitted for admin approval");
        return { ok: true, requested: true };
    } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Could not submit request");
        return { ok: false };
    }
}
